import { useState, type ReactNode } from 'react';
import {
  SENIORITY_COLORS,
  SENIORITY_LEVELS,
  filterItems,
  marginPct,
  type ContractFinance,
  type Seniority,
} from '@forecastify/shared';
import {
  Badge,
  DataList,
  type Cell,
  type DataListColumn,
  type DataListRow,
  type SortState,
} from '@forecastify/ui';
import { useT } from '../../../i18n';
import { listLabels } from '../../delivery/clients-screen';
import { sortInMemory } from '../../sort-in-memory';
import { useRowPages } from '../../use-row-pages';
import { FinanceTip, financeTipContent, type FinanceTipText } from './finance-tip';
import {
  REGISTER_PAGE_SIZE,
  lineItemTypeLabel,
  lineItemTypeOptions,
  matches,
} from '../register-tools';
import {
  barWidth,
  index,
  money,
  percent,
  signedMoney,
  useHourFormat,
  type Currency,
} from './finance-format';
import type { FinanceModel } from './finance-model';

/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ C58 · the profitability table, and where the money went.                  │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * ⚠️⚠️ **PERIODS FOR A RECURRING CONTRACT, LINE ITEMS FOR EVERYTHING ELSE — AND THE COLUMNS ARE
 * NOT THE SAME.** A period has a status and an over-frame column and no CPI; a line item has a
 * type, an earned value and a CPI and no status. Forcing one table to serve both would mean eight
 * columns that are blank for half the rows, which is how a table stops being read.
 *
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ ⚠️⚠️ `DataList` NOW — ux-pass-2, 14 Oct 2026, overruling the note that stood here. │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * It called this a ledger, *"where sorting by margin would break the chronology"*. The product
 * owner asked for the unified list here by name — search, filters, the multi-column header sort
 * and the settled pager. An unsorted list keeps the wire's order, which IS the chronology, and
 * `sortInMemory` returns exactly that for an empty sort.
 *
 * ⚠️ **The column tooltips came along** — every head keeps its `?` through `DataListColumn.tip`,
 * built from the same `tip()` entries in `FinanceTip`'s own layout. What the move cost is the row
 * tints: `DataList` styles its own rows, so the running period's tint became a filled badge, and a
 * row outside the Σ is faded cell by cell rather than at the `<tr>`.
 */

/** A money figure in a column. `faded` is a row the Σ does not count — see the `scope` prop. */
function figure(text: string, faded: boolean, extra: Partial<Cell> = {}): Cell {
  return {
    text,
    mono: true,
    ...extra,
    className: [extra.className ?? '', faded ? 'opacity-45' : ''].join(' ').trim(),
  };
}

/** The columns and totals made of income: hidden on an internal project. */
const INCOME_COLUMNS: ReadonlySet<string> = new Set(['revenue', 'billable', 'margin', 'marginPct']);

export function FinanceTable({
  view,
  currency,
  scope,
  onOpenPeriod,
  onOpenLineItem,
  includeExternal,
  internal = false,
}: {
  readonly view: ContractFinance;
  readonly currency: Currency;
  /** 21 Oct 2026 · an internal project earns nothing: no income or margin columns or totals. */
  readonly internal?: boolean;
  /**
   * ⚠⚠ **THE Σ ROW FOLLOWS THE SCOPE, AND IT USED NOT TO.** The table lists every period and
   * every deliverable of the contract whatever the scope selector says — deliberately, because it
   * is the contract's register — but its footer summed all of them too, while every KPI tile above
   * it was about the selected scope. Two totals on one screen, both correct, describing different
   * things, with nothing to say which was which. The rows the footer counts are marked, so the
   * reader can see the total and its addends at once.
   */
  readonly scope: {
    readonly lineItemIds?: readonly string[];
    readonly occurrenceStartsOn?: readonly string[];
  };
  readonly onOpenPeriod: (startsOn: string) => void;
  readonly onOpenLineItem: (id: string) => void;
  readonly includeExternal: boolean;
}) {
  const t = useT();

  /**
   * ┌─────────────────────────────────────────────────────────┐
   * │ ⚠️⚠️ BOTH ARMS' SEARCH, FILTERS, SORT AND PAGER — round 5 item 24, ux-pass-2.  │
   * └─────────────────────────────────────────────────────────┘
   *
   * ⚠️⚠️ **DECLARED ABOVE THE `recurring` BRANCH ON PURPOSE.** The two arms below return from
   * different points, and a hook called inside one of them is a hook that runs on some renders and
   * not others — React's one unbreakable rule. Each arm's state is its own, and the arm that does
   * not draw costs a filter over an empty array.
   */
  const [periodQuery, setPeriodQuery] = useState('');
  const [periodState, setPeriodState] = useState('');
  const [periodSort, setPeriodSort] = useState<readonly SortState[]>([]);
  const [itemQuery, setItemQuery] = useState('');
  const [itemType, setItemType] = useState('');
  const [itemSort, setItemSort] = useState<readonly SortState[]>([]);

  const inScope = (row: { startsOn?: string; id?: string }): boolean => {
    if (scope.occurrenceStartsOn !== undefined) {
      return row.startsOn !== undefined && scope.occurrenceStartsOn.includes(row.startsOn);
    }
    if (scope.lineItemIds !== undefined) {
      return row.id !== undefined && scope.lineItemIds.includes(row.id);
    }
    return true;
  };
  const m = (value: number | null): string => money(value, currency);
  const s = (value: number | null): string => signedMoney(value, currency);
  /* A column head's explanation, in `FinanceTip`'s own layout — the `?` beside the heading. */
  const head = (key: string): ReactNode => financeTipContent(tip(t, key), t('finance.calc'));

  /* ── the periods ─────────────────────────────────────────────────────────── */

  const shownPeriods = view.periods.filter(
    (row) =>
      /* 3 Nov 2026 · several states at once — OR within. */
      (periodState === '' || filterItems(periodState).includes(row.state)) &&
      /* ⚠️ Both dates, the ordinal and the invoice number — the four things visible on the row
         that somebody would type. `#3` matches because the `#` is part of the haystack. */
      matches(
        [`#${String(row.index)}`, row.startsOn, row.endsOn, row.invoiceExternalId],
        periodQuery,
      ),
  );
  const periodRevenue = (row: (typeof view.periods)[number]): number =>
    Number(row.revenue) + Number(row.billableRevenue);

  const periodRows: DataListRow[] = sortInMemory(shownPeriods, periodSort, {
    index: (row) => row.index,
    period: (row) => row.startsOn,
    status: (row) => (row.state === 'past' ? 0 : row.state === 'current' ? 1 : 2),
    budget: (row) => Number(row.budget),
    revenue: (row) => Number(row.revenue),
    billable: (row) => Number(row.billableRevenue),
    spend: (row) => Number(row.cost),
    external: (row) => Number(row.externalCost),
    margin: (row) => periodRevenue(row) - Number(row.cost),
    marginPct: (row) =>
      periodRevenue(row) > 0.4
        ? (periodRevenue(row) - Number(row.cost)) / periodRevenue(row)
        : null,
    invoiced: (row) => Number(row.invoiced),
    invoice: (row) => row.invoiceExternalId,
  }).map((row) => {
    const revenue = periodRevenue(row);
    const margin = revenue - Number(row.cost);
    const faded = !inScope(row);
    const future = row.state === 'future';
    const plain = (text: string, extra: Partial<Cell> = {}): Cell =>
      figure(text, faded, { muted: future, ...extra });
    return {
      id: row.startsOn,
      cells: {
        index: plain(`#${String(row.index)}`),
        period: plain(`${row.startsOn} – ${row.endsOn}`),
        /* ⚠️ The CURRENT period is the filled badge — a reader looking for "where are we now"
           should not have to compare dates. It used to be a tinted row. */
        status: {
          badge: {
            label: `${
              row.state === 'past'
                ? t('finance.state.past')
                : row.state === 'current'
                  ? t('finance.state.current')
                  : t('finance.state.future')
            }${row.reopened ? ' ·' : ''}`,
            variant:
              row.state === 'current'
                ? ('default' as const)
                : future
                  ? ('outline' as const)
                  : ('secondary' as const),
          },
        },
        /* ⚠️ A pencil marks a figure somebody TYPED — it will not move when the line item is
           re-priced, and that is the one thing a reader must know. */
        budget: plain(`${m(Number(row.budget))}${row.budgetOverridden ? ' ✎' : ''}`),
        revenue: plain(`${m(Number(row.revenue))}${row.revenueOverridden ? ' ✎' : ''}`),
        billable: Number(row.billableRevenue) > 0.4 ? plain(m(Number(row.billableRevenue))) : null,
        spend: plain(m(Number(row.cost)), {
          className: Number(row.cost) > Number(row.budget) + 0.4 ? 'text-destructive' : '',
        }),
        external: Number(row.externalCost) > 0.4 ? plain(m(Number(row.externalCost))) : null,
        margin: plain(s(margin), { className: margin < -0.4 ? 'text-destructive' : '' }),
        marginPct: revenue > 0.4 ? plain(percent(margin / revenue)) : null,
        invoiced: Number(row.invoiced) > 0.4 ? plain(m(Number(row.invoiced))) : null,
        invoice: row.invoiceExternalId === null ? null : plain(row.invoiceExternalId),
      },
    };
  });
  const pagedPeriods = useRowPages(
    periodRows,
    JSON.stringify([periodQuery, periodState, periodSort]),
    REGISTER_PAGE_SIZE,
  );

  /* ── the line items ──────────────────────────────────────────────────────── */

  const shownItems = view.lineItems.filter(
    (row) =>
      (itemType === '' || filterItems(itemType).includes(row.type)) &&
      matches([row.name, row.identifier, row.invoiceExternalId], itemQuery),
  );
  const billableOf = (row: (typeof view.lineItems)[number]): boolean => row.type !== 'non_billable';
  const marginOf = (row: (typeof view.lineItems)[number]): number =>
    Number(row.revenue) - Number(row.cost);
  const cpiOf = (row: (typeof view.lineItems)[number]): number | null =>
    Number(row.cost) > 0.4 && Number(row.budget) > 0.4
      ? Number(row.earnedValue) / Number(row.cost)
      : null;
  const marginPctOf = (row: (typeof view.lineItems)[number]): number | null =>
    billableOf(row) && marginPct(Number(row.revenue), Number(row.cost)) !== null
      ? marginOf(row) / Number(row.revenue)
      : null;

  const itemRows: DataListRow[] = sortInMemory(shownItems, itemSort, {
    name: (row) => row.name,
    type: (row) => lineItemTypeLabel(t, row.type),
    revenue: (row) => (billableOf(row) ? Number(row.revenue) : null),
    budget: (row) => (row.type === 'time_and_material' ? null : Number(row.budget)),
    planned: (row) => Number(row.plannedCost),
    spend: (row) => Number(row.cost),
    external: (row) => Number(row.externalCost),
    ev: (row) => (Number(row.budget) > 0.4 ? Number(row.earnedValue) : null),
    cpi: cpiOf,
    margin: (row) => (billableOf(row) ? marginOf(row) : null),
    marginPct: marginPctOf,
    invoiced: (row) => Number(row.invoiced),
    invoice: (row) => row.invoiceExternalId,
  }).map((row) => {
    const billable = billableOf(row);
    const margin = marginOf(row);
    const faded = !inScope(row);
    const cpi = cpiOf(row);
    const pct = marginPctOf(row);
    return {
      id: row.id,
      cells: {
        name: figure(row.name, faded, { mono: false, bold: true }),
        type: figure(lineItemTypeLabel(t, row.type), faded, { mono: false }),
        revenue: billable ? figure(m(Number(row.revenue)), faded) : null,
        /* ⚠️ T&M has no frame — its revenue comes from the fact, so a budget column would invite
           a comparison that means nothing. */
        budget: row.type === 'time_and_material' ? null : figure(m(Number(row.budget)), faded),
        planned: figure(m(Number(row.plannedCost)), faded),
        spend: figure(m(Number(row.cost)), faded, {
          className:
            Number(row.budget) > 0.4 && Number(row.cost) > Number(row.budget) + 0.4
              ? 'text-destructive'
              : '',
        }),
        external:
          Number(row.externalCost) > 0.4 ? figure(m(Number(row.externalCost)), faded) : null,
        ev: Number(row.budget) > 0.4 ? figure(m(Number(row.earnedValue)), faded) : null,
        cpi: cpi === null ? null : figure(index(cpi), faded),
        margin: billable
          ? figure(s(margin), faded, { className: margin < -0.4 ? 'text-destructive' : '' })
          : null,
        marginPct: pct === null ? null : figure(percent(pct), faded),
        invoiced: Number(row.invoiced) > 0.4 ? figure(m(Number(row.invoiced)), faded) : null,
        invoice: row.invoiceExternalId === null ? null : figure(row.invoiceExternalId, faded),
      },
    };
  });
  const pagedItems = useRowPages(
    itemRows,
    JSON.stringify([itemQuery, itemType, itemSort]),
    REGISTER_PAGE_SIZE,
  );

  const noMatch = {
    icon: 'search' as const,
    title: t('effort.tools.noMatch'),
    body: t('effort.tools.noMatchBody'),
  };

  if (view.recurring) {
    const columns: DataListColumn[] = [
      { key: 'index', tip: head('periodIndex'), label: '#', width: 56, sortKey: 'index' },
      {
        key: 'period',
        tip: head('period'),
        label: t('finance.col.period'),
        width: 190,
        sortKey: 'period',
      },
      {
        key: 'status',
        tip: head('periodStatus'),
        label: t('finance.col.status'),
        width: 112,
        sortKey: 'status',
      },
      {
        key: 'budget',
        tip: head('periodBudget'),
        label: t('finance.col.budget'),
        width: 120,
        align: 'right',
        sortKey: 'budget',
      },
      {
        key: 'revenue',
        tip: head('periodRevenue'),
        label: t('finance.col.revenue'),
        width: 120,
        align: 'right',
        sortKey: 'revenue',
      },
      {
        key: 'billable',
        tip: head('periodBillable'),
        label: t('finance.col.billable'),
        width: 130,
        align: 'right',
        sortKey: 'billable',
      },
      {
        key: 'spend',
        tip: head('periodSpendCol'),
        label: t('finance.col.spend'),
        width: 120,
        align: 'right',
        sortKey: 'spend',
      },
      {
        key: 'external',
        tip: head('periodExternal'),
        label: t('finance.col.external'),
        width: 136,
        align: 'right',
        sortKey: 'external',
      },
      {
        key: 'margin',
        tip: head('periodMargin'),
        label: t('finance.col.margin'),
        width: 120,
        align: 'right',
        sortKey: 'margin',
      },
      {
        key: 'marginPct',
        tip: head('periodMarginPct'),
        label: t('finance.col.marginPct'),
        width: 96,
        align: 'right',
        sortKey: 'marginPct',
      },
      {
        key: 'invoiced',
        tip: head('periodInvoiced'),
        label: t('finance.col.invoiced'),
        width: 120,
        align: 'right',
        sortKey: 'invoiced',
      },
      {
        key: 'invoice',
        tip: head('periodInvoice'),
        label: t('finance.col.invoice'),
        width: 128,
        sortKey: 'invoice',
      },
    ];

    return (
      <section className="bg-card overflow-hidden rounded-xl border shadow-sm">
        <div className="flex items-center gap-2 border-b px-4 py-2.5">
          <div className="text-muted-foreground text-[11px] font-medium tracking-wide uppercase">
            {t('finance.table.periods')}
          </div>
          <span className="text-muted-foreground text-[11px] tabular-nums">
            {t('finance.table.periodCount', { count: view.periods.length })}
          </span>
        </div>

        {/**
         * ⚠️⚠️ **NO Σ ROW — round 5 item 24, and still none.** Every one of the five it used to
         * carry is a KPI tile above this table, computed over the whole contract; a footer summing
         * them again would be the same five numbers a second time, and the less trustworthy copy.
         */}
        <DataList
          /* 30 Oct 2026 · sized to its rows: `h-full` took the card's whole height BELOW its header,
             so the pager overflowed the card and was clipped — only ten rows could ever be seen. */
          className="h-auto"
          columnsKey="finance.periods"
          selectable={false}
          columns={internal ? columns.filter((one) => !INCOME_COLUMNS.has(one.key)) : columns}
          rows={pagedPeriods.rows}
          page={pagedPeriods.page}
          sort={periodSort}
          onSortChange={setPeriodSort}
          search={periodQuery}
          onSearchChange={setPeriodQuery}
          filters={[
            {
              key: 'state',
              /* ⚠️ The three states the row already prints, as the filter's three options — a
                 fourth vocabulary for the same column is how a filter starts lying. */
              label: t('finance.filter.state'),
              kind: 'multi' as const,
              value: periodState,
              onChange: setPeriodState,
              options: [
                { value: 'past', label: t('finance.state.past') },
                { value: 'current', label: t('finance.state.current') },
                { value: 'future', label: t('finance.state.future') },
              ],
            },
          ]}
          onRowClick={onOpenPeriod}
          empty={
            view.periods.length === 0
              ? { icon: 'calendar', title: t('effort.tools.noPeriods'), body: '' }
              : noMatch
          }
          labels={{ ...listLabels(t), table: t('finance.table.periods') }}
        />
      </section>
    );
  }

  /**
   * ⚠️⚠️ **THE Σ COUNTS THE FILTERED SET THAT IS IN SCOPE — NOT THE PAGE.** Two narrowings meet
   * here and both apply: the scope selector above (rows outside it are faded, see the `scope`
   * prop) and this list's own search and type filter. A total over the page would change when
   * somebody pressed Next; a total that ignored the filter would describe rows the reader just hid.
   * It sits in the list's footer strip, beside the pager's count of the same set.
   */
  const counted = shownItems.filter((row) => inScope(row));
  const total = (pick: (row: (typeof view.lineItems)[number]) => string): number =>
    counted.reduce((sum, row) => sum + Number(pick(row)), 0);
  const totalMargin = counted.reduce(
    (sum, row) => sum + (row.type === 'non_billable' ? 0 : marginOf(row)),
    0,
  );

  const columns: DataListColumn[] = [
    { key: 'name', tip: head('itemName'), label: t('finance.col.name'), sortKey: 'name' },
    {
      key: 'type',
      tip: head('itemType'),
      label: t('finance.col.type'),
      width: 140,
      sortKey: 'type',
    },
    {
      key: 'revenue',
      tip: head('itemRevenue'),
      label: t('finance.col.revenue'),
      width: 120,
      align: 'right',
      sortKey: 'revenue',
    },
    {
      key: 'budget',
      tip: head('itemBudget'),
      label: t('finance.col.budget'),
      width: 120,
      align: 'right',
      sortKey: 'budget',
    },
    {
      key: 'planned',
      tip: head('itemPlanned'),
      label: t('finance.col.planned'),
      width: 150,
      align: 'right',
      sortKey: 'planned',
    },
    {
      key: 'spend',
      tip: head('itemSpend'),
      label: t('finance.col.spend'),
      width: 120,
      align: 'right',
      sortKey: 'spend',
    },
    {
      key: 'external',
      tip: head('itemExternal'),
      label: t('finance.col.external'),
      width: 136,
      align: 'right',
      sortKey: 'external',
    },
    {
      key: 'ev',
      tip: head('itemEv'),
      label: t('finance.col.ev'),
      width: 110,
      align: 'right',
      sortKey: 'ev',
    },
    {
      key: 'cpi',
      tip: head('itemCpi'),
      label: t('finance.col.cpi'),
      width: 72,
      align: 'right',
      sortKey: 'cpi',
    },
    {
      key: 'margin',
      tip: head('itemMargin'),
      label: t('finance.col.margin'),
      width: 120,
      align: 'right',
      sortKey: 'margin',
    },
    {
      key: 'marginPct',
      tip: head('itemMarginPct'),
      label: t('finance.col.marginPct'),
      width: 96,
      align: 'right',
      sortKey: 'marginPct',
    },
    {
      key: 'invoiced',
      tip: head('itemInvoiced'),
      label: t('finance.col.invoiced'),
      width: 120,
      align: 'right',
      sortKey: 'invoiced',
    },
    {
      key: 'invoice',
      tip: head('itemInvoice'),
      label: t('finance.col.invoice'),
      width: 128,
      sortKey: 'invoice',
    },
  ];

  return (
    <section className="bg-card overflow-hidden rounded-xl border shadow-sm">
      <div className="flex items-center gap-2 border-b px-4 py-2.5">
        <div className="text-muted-foreground text-[11px] font-medium tracking-wide uppercase">
          {t('finance.table.lineItems')}
        </div>
        <span className="text-muted-foreground text-[11px] tabular-nums">
          {t('finance.table.lineItemCount', { count: view.lineItems.length })}
        </span>
      </div>

      <DataList
        /* 30 Oct 2026 · sized to its rows: `h-full` took the card's whole height BELOW its header,
           so the pager overflowed the card and was clipped — only ten rows could ever be seen. */
        className="h-auto"
        columnsKey="finance.lineItems"
        selectable={false}
        columns={internal ? columns.filter((one) => !INCOME_COLUMNS.has(one.key)) : columns}
        rows={pagedItems.rows}
        page={pagedItems.page}
        sort={itemSort}
        onSortChange={setItemSort}
        search={itemQuery}
        onSearchChange={setItemQuery}
        filters={[
          {
            key: 'type',
            label: t('finance.filter.type'),
            kind: 'multi' as const,
            value: itemType,
            onChange: setItemType,
            options: lineItemTypeOptions(t),
          },
        ]}
        onRowClick={onOpenLineItem}
        empty={
          view.lineItems.length === 0
            ? {
                icon: 'file-text',
                title: t('effort.empty.noLineItem'),
                body: t('effort.empty.noLineItemBody'),
              }
            : noMatch
        }
        footerNote={
          <Totals
            items={[
              {
                key: 'revenue',
                label: t('finance.total.revenue'),
                value: m(total((row) => row.revenue)),
              },
              {
                key: 'budget',
                label: t('finance.total.budget'),
                value: m(total((row) => row.budget)),
              },
              { key: 'spend', label: t('finance.total.spend'), value: m(total((row) => row.cost)) },
              { key: 'margin', label: t('finance.total.margin'), value: s(totalMargin) },
              {
                key: 'invoiced',
                label: t('finance.total.invoiced'),
                value: m(total((row) => row.invoiced)),
              },
            ].filter((one) => !internal || !INCOME_COLUMNS.has(one.key))}
          />
        }
        labels={{ ...listLabels(t), table: t('finance.table.lineItems') }}
      />
      {includeExternal ? null : (
        <p className="text-muted-foreground border-t px-4 py-2 text-[11px]">
          {t('finance.table.externalExcluded')}
        </p>
      )}
    </section>
  );
}

/** The Σ figures, inline in the list's footer strip. */
function Totals({
  items,
}: {
  readonly items: readonly { key: string; label: string; value: string }[];
}) {
  return (
    <span className="flex flex-wrap items-center gap-x-5 gap-y-1">
      {items.map((item) => (
        <span key={item.key}>
          <span>{item.label} </span>
          <span className="text-foreground tabular-nums">{item.value}</span>
        </span>
      ))}
    </span>
  );
}

/* ── the cost breakdown ────────────────────────────────────────────────────── */

/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ ⚠️ KÖLTÉS-BONTÁS — where the money went, by person or by role.            │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * ⚠️⚠️ **THREE DIMENSIONS, AND THE FIRST ONE IS WHY `0135` EXISTS.** *Szenioritás* was recorded as
 * owed when C58 shipped, because there was no field to group by — the prototype holds the level as
 * React state. It is a column on a member now, and the grouping is a key rather than a second read:
 * the wire is per PERSON and this regroups it, so all three tabs are guaranteed to sum alike.
 *
 * ⚠️ **A person with no level is their own group, not folded into `medior`.** The mockup defaults a
 * missing level; asserting a seniority about somebody nobody has classified is exactly what `0135`
 * refused to do on a migration, and a screen must not do it either.
 */
export function CostBreakdown({
  view,
  model,
  currency,
}: {
  readonly view: ContractFinance;
  readonly model: FinanceModel;
  readonly currency: Currency;
}) {
  return (
    /**
     * ┌───────────────────────────────────────────────────────────────────────────┐
     * │ ⚠️⚠️ THREE SEPARATE PANELS — item 25, second pass.                        │
     * └───────────────────────────────────────────────────────────────────────────┘
     *
     * The first pass took the tab strip off and left all three tables inside one card, which is
     * not what was asked for and does not read as what was asked for: three header rows buried in
     * one panel look like sub-groups of the first table, and there is no edge saying where
     * *szenioritás* ends and *szerepkör* begins. *"I need this in 3 fully separate tables."*
     *
     * ⚠️ **Each is its own card now**, with the gap the rest of the screen uses between panels.
     *
     * ⚠️ **The panel's NAME is on the first only.** All three are the same breakdown cut
     * differently, and three copies of *Költés-bontás* with the same tooltip under each would be
     * the same sentence offered three times. The first column of every table already says which
     * cut it is.
     *
     * ⚠️ **They are guaranteed to sum alike**, which is what makes three panels honest rather than
     * three different answers: all three regroup the SAME per-person rows off the wire.
     */
    <div className="flex flex-col gap-3">
      {(['seniority', 'role', 'person'] as const).map((by, index) => (
        <BreakdownTable
          key={by}
          by={by}
          view={view}
          model={model}
          currency={currency}
          titled={index === 0}
          /* ⚠️ The understated-cost warning goes under the LAST one: it is a fact about the
             contract's data rather than about any one grouping, so it is said once. */
          warn={index === 2}
        />
      ))}
    </div>
  );
}

/** One cut of the same rows — its first column's heading names the dimension. */
function BreakdownTable({
  by,
  view,
  model,
  currency,
  titled,
  warn,
}: {
  readonly by: 'seniority' | 'role' | 'person';
  readonly view: ContractFinance;
  readonly model: FinanceModel;
  readonly currency: Currency;
  /** ⚠️ The panel's own name, on the first of the three only — see `CostBreakdown`. */
  readonly titled: boolean;
  /** ⚠️ The understated-cost badge, under the last of the three only. */
  readonly warn: boolean;
}) {
  const { hours } = useHourFormat();
  const t = useT();
  const m = (value: number | null): string => money(value, currency);

  const groups = new Map<
    string,
    {
      label: string;
      key: string;
      /** The level's own colour, where the grouping has one. */
      dot: string | null;
      people: Set<string>;
      plannedHours: number;
      plannedCost: number;
      loggedHours: number;
      actualCost: number;
    }
  >();
  for (const person of view.people) {
    const key =
      by === 'person'
        ? person.memberId
        : by === 'role'
          ? (person.roleName ?? '–')
          : (person.seniority ?? '–');
    const label =
      by === 'person'
        ? person.name
        : by === 'role'
          ? (person.roleName ?? t('finance.breakdown.noRole'))
          : seniorityLabel(t, person.seniority);
    const group = groups.get(key) ?? {
      label,
      key,
      /* ⚠️ The mockup's own colours, and only where the grouping HAS one — a dot beside a
         person's name would be a colour that means nothing. A project role carries its own. */
      dot:
        by === 'seniority' && person.seniority !== null
          ? SENIORITY_COLORS[person.seniority]
          : by === 'role'
            ? person.roleColor
            : null,
      people: new Set<string>(),
      plannedHours: 0,
      plannedCost: 0,
      loggedHours: 0,
      actualCost: 0,
    };
    group.people.add(person.memberId);
    group.plannedHours += Number(person.plannedHours);
    group.plannedCost += Number(person.plannedCost);
    group.loggedHours += Number(person.loggedHours);
    group.actualCost += Number(person.actualCost);
    groups.set(key, group);
  }

  /**
   * ⚠️ **By SPEND, except for seniority, which keeps its own ladder.** Junior-to-principal is a
   * scale a reader already holds in their head; re-ordering it by cost every time the data moves
   * makes the panel unreadable at a glance, which is the one thing a breakdown is for.
   */
  const rows = [...groups.values()].sort((one, other) =>
    by === 'seniority'
      ? ladder(one.key) - ladder(other.key)
      : other.actualCost + other.plannedCost - (one.actualCost + one.plannedCost),
  );

  /* The first column is named after the dimension this table cuts by, so the heading and the rows
     cannot disagree about what the rows are. */
  const groupLabel =
    by === 'seniority'
      ? t('finance.breakdown.bySeniority')
      : by === 'role'
        ? t('finance.breakdown.byRole')
        : t('finance.breakdown.byPerson');

  if (rows.length === 0) {
    return (
      <section className="bg-card text-card-foreground overflow-hidden rounded-xl border shadow-sm">
        <Title titled={titled} />
        <div className="text-muted-foreground bg-muted/40 border-b px-4 py-1.5 text-[10.5px] font-medium tracking-wide uppercase">
          {groupLabel}
        </div>
        <p className="text-muted-foreground px-4 py-6 text-center text-[12px]">
          {t('finance.breakdown.empty')}
        </p>
      </section>
    );
  }

  return (
    <section className="bg-card text-card-foreground flex flex-col overflow-x-auto rounded-xl border shadow-sm">
      <Title titled={titled} />
      {/**
       * ⚠️⚠️ **SIX COLUMNS AND NOT ONE OF THEM WAS LABELLED.** The mockup gives this breakdown a
       * header row where every column carries its own explanation (`markup.html`'s `sen.head`,
       * six `TIP(...)` entries) — ours drew the figures and left the reader to infer which was
       * planned and which was actual from their order. Reported as *"some elements don't even
       * have an info hint"*, which understates it: they had no NAME either.
       *
       * ⚠️ **And it is now each table's title too**, since the tab strip that used to name the
       * dimension is gone — the first cell carries `groupLabel`.
       */}
      <div className="text-muted-foreground bg-muted/40 grid min-w-[460px] grid-cols-[minmax(0,1fr)_56px_96px_96px_minmax(0,120px)_52px] items-center gap-2 border-b px-4 py-1.5 text-[10.5px] font-medium tracking-wide uppercase">
        <span>
          <FinanceTip tip={tip(t, 'breakdownGroup')} calcLabel={t('finance.calc')}>
            {groupLabel}
          </FinanceTip>
        </span>
        <span className="text-right">
          <FinanceTip tip={tip(t, 'breakdownPeople')} calcLabel={t('finance.calc')}>
            {t('finance.breakdown.col.people')}
          </FinanceTip>
        </span>
        <span className="text-right">
          <FinanceTip tip={tip(t, 'breakdownPlanned')} calcLabel={t('finance.calc')}>
            {t('finance.breakdown.col.planned')}
          </FinanceTip>
        </span>
        <span className="text-right">
          <FinanceTip tip={tip(t, 'breakdownLogged')} calcLabel={t('finance.calc')}>
            {t('finance.breakdown.col.logged')}
          </FinanceTip>
        </span>
        <span className="text-right">
          <FinanceTip tip={tip(t, 'breakdownCost')} calcLabel={t('finance.calc')}>
            {t('finance.breakdown.col.cost')}
          </FinanceTip>
        </span>
        <span className="text-right">
          <FinanceTip tip={tip(t, 'breakdownShare')} calcLabel={t('finance.calc')}>
            {t('finance.breakdown.col.share')}
          </FinanceTip>
        </span>
      </div>

      {rows.map((row) => (
        <div
          key={row.label}
          className="grid min-w-[460px] grid-cols-[minmax(0,1fr)_56px_96px_96px_minmax(0,120px)_52px] items-center gap-2 border-b px-4 py-2 text-[11.5px] last:border-0"
        >
          <span className="flex min-w-0 items-center gap-1.5 font-medium">
            {row.dot === null ? null : (
              <span className="size-2 shrink-0 rounded-full" style={{ background: row.dot }} />
            )}
            <span className="truncate">{row.label}</span>
          </span>
          <span className="text-muted-foreground text-right tabular-nums">
            {t('finance.breakdown.headcount', { count: row.people.size })}
          </span>
          <span className="text-muted-foreground text-right tabular-nums">
            {hours(row.plannedHours)}
          </span>
          <span className="text-right tabular-nums">{hours(row.loggedHours)}</span>
          <span className="text-right tabular-nums">{m(row.actualCost)}</span>
          <span className="text-muted-foreground text-right tabular-nums">
            {model.actualCost > 0.4
              ? `${String(Math.round((row.actualCost / model.actualCost) * 100))}%`
              : '–'}
          </span>
          <span className="col-span-6">
            <span className="bg-muted block h-1 overflow-hidden rounded-full">
              <span
                className="bg-primary/70 block h-full"
                style={{ width: `${String(barWidth(row.actualCost, model.actualCost))}%` }}
              />
            </span>
          </span>
        </div>
      ))}

      {!warn || view.unratedMembers === 0 ? null : (
        /**
         * ⚠️⚠️ **THE COST IS UNDERSTATED AND THE PANEL HAS TO SAY SO.** Hours logged by
         * somebody with no rate card in force are counted in the hours and priced at nothing — a
         * reader comparing a healthy margin against their own memory would be reading a figure
         * that is missing a person, with the screen looking complete.
         */
        <p className="text-muted-foreground border-t px-4 py-2 text-[11px]">
          <Badge variant="outline">
            {t('finance.breakdown.unrated', { count: view.unratedMembers })}
          </Badge>
        </p>
      )}
    </section>
  );
}

/** The breakdown's own name and explanation — drawn once, over the first of the three panels. */
function Title({ titled }: { readonly titled: boolean }) {
  const t = useT();
  if (!titled) return null;
  return (
    <div className="flex flex-wrap items-center gap-2 border-b px-4 py-2.5">
      <div className="text-muted-foreground text-[11px] font-medium tracking-wide uppercase">
        <FinanceTip
          calcLabel={t('finance.calc')}
          tip={{
            name: t('finance.tip.breakdown.name'),
            what: t('finance.tip.breakdown.what'),
            calc: t('finance.tip.breakdown.calc'),
          }}
        >
          {t('finance.breakdown.title')}
        </FinanceTip>
      </div>
    </div>
  );
}

/** The column tooltips, kept out of the table body so the markup stays legible. */
function tip(t: ReturnType<typeof useT>, key: string): FinanceTipText {
  const map: Record<string, FinanceTipText> = {
    /* The cost breakdown's own six — the mockup's `sen.head`. */
    breakdownGroup: {
      name: t('finance.tip.bd.group.name'),
      what: t('finance.tip.bd.group.what'),
    },
    breakdownPeople: {
      name: t('finance.tip.bd.people.name'),
      what: t('finance.tip.bd.people.what'),
      calc: t('finance.tip.bd.people.calc'),
    },
    breakdownPlanned: {
      name: t('finance.tip.bd.planned.name'),
      what: t('finance.tip.bd.planned.what'),
      calc: t('finance.tip.bd.planned.calc'),
    },
    breakdownLogged: {
      name: t('finance.tip.bd.logged.name'),
      what: t('finance.tip.bd.logged.what'),
      calc: t('finance.tip.bd.logged.calc'),
    },
    breakdownCost: {
      name: t('finance.tip.bd.cost.name'),
      what: t('finance.tip.bd.cost.what'),
      calc: t('finance.tip.bd.cost.calc'),
      note: t('finance.tip.bd.cost.note'),
    },
    breakdownShare: {
      name: t('finance.tip.bd.share.name'),
      what: t('finance.tip.bd.share.what'),
      calc: t('finance.tip.bd.share.calc'),
    },
    periodIndex: { name: t('finance.tip.col.index.name'), what: t('finance.tip.col.index.what') },
    period: { name: t('finance.tip.col.period.name'), what: t('finance.tip.col.period.what') },
    periodStatus: {
      name: t('finance.tip.col.status.name'),
      what: t('finance.tip.col.status.what'),
      note: t('finance.tip.col.status.note'),
    },
    periodBudget: {
      name: t('finance.tip.col.periodBudget.name'),
      what: t('finance.tip.col.periodBudget.what'),
      calc: t('finance.tip.col.periodBudget.calc'),
      note: t('finance.tip.col.edited'),
    },
    periodRevenue: {
      name: t('finance.tip.col.periodRevenue.name'),
      what: t('finance.tip.col.periodRevenue.what'),
      calc: t('finance.tip.col.periodRevenue.calc'),
    },
    periodBillable: {
      name: t('finance.tip.col.billable.name'),
      what: t('finance.tip.col.billable.what'),
      note: t('finance.tip.col.billable.note'),
    },
    periodSpendCol: {
      name: t('finance.tip.actualCost.name'),
      what: t('finance.tip.col.spend.what'),
      calc: t('finance.tip.actualCost.calc'),
    },
    periodExternal: {
      name: t('finance.tip.external.name'),
      what: t('finance.tip.external.what'),
      calc: t('finance.tip.col.external.calc'),
    },
    periodMargin: {
      name: t('finance.tip.col.margin.name'),
      what: t('finance.tip.col.margin.what'),
      calc: t('finance.tip.col.periodMargin.calc'),
      note: t('finance.tip.col.margin.note'),
    },
    periodMarginPct: {
      name: t('finance.tip.col.marginPct.name'),
      what: t('finance.tip.col.marginPct.what'),
      calc: t('finance.tip.col.periodMarginPct.calc'),
    },
    periodInvoiced: {
      name: t('finance.tip.invoiced.name'),
      what: t('finance.tip.col.periodInvoiced.what'),
      calc: t('finance.tip.invoiced.calc'),
    },
    periodInvoice: {
      name: t('finance.tip.col.invoice.name'),
      what: t('finance.tip.col.invoice.what'),
      note: t('finance.tip.col.invoice.note'),
    },
    itemName: { name: t('finance.tip.col.item.name'), what: t('finance.tip.col.item.what') },
    itemType: {
      name: t('finance.tip.col.itemType.name'),
      what: t('finance.tip.col.itemType.what'),
      note: t('finance.tip.col.itemType.note'),
    },
    itemRevenue: {
      name: t('finance.tip.revenue.name'),
      what: t('finance.tip.col.itemRevenue.what'),
      calc: t('finance.tip.col.itemRevenue.calc'),
      note: t('finance.tip.col.itemRevenue.note'),
    },
    itemBudget: {
      name: t('finance.tip.col.itemBudget.name'),
      what: t('finance.tip.col.itemBudget.what'),
      calc: t('finance.tip.col.itemBudget.calc'),
      note: t('finance.tip.col.itemBudget.note'),
    },
    itemPlanned: {
      name: t('finance.tip.plannedCost.name'),
      what: t('finance.tip.plannedCost.what'),
      calc: t('finance.tip.plannedCost.calc'),
    },
    itemSpend: {
      name: t('finance.tip.actualCost.name'),
      what: t('finance.tip.col.spend.what'),
      calc: t('finance.tip.actualCost.calc'),
    },
    itemExternal: {
      name: t('finance.tip.external.name'),
      what: t('finance.tip.external.what'),
      calc: t('finance.tip.col.itemExternal.calc'),
    },
    itemEv: {
      name: t('finance.tip.ev.name'),
      what: t('finance.tip.ev.what'),
      calc: t('finance.tip.ev.calc'),
    },
    itemCpi: {
      name: t('finance.tip.cpi.name'),
      what: t('finance.tip.col.itemCpi.what'),
      /* ⚠️ The column divides MONEY (the row's earned value by its cost) — not the tile's hours. */
      calc: t('finance.tip.col.itemCpi.calc'),
      note: t('finance.tip.col.itemCpi.note'),
    },
    itemMargin: {
      name: t('finance.tip.col.margin.name'),
      what: t('finance.tip.col.margin.what'),
      calc: t('finance.tip.col.itemMargin.calc'),
    },
    itemMarginPct: {
      name: t('finance.tip.col.marginPct.name'),
      what: t('finance.tip.col.marginPct.what'),
      calc: t('finance.tip.col.itemMarginPct.calc'),
    },
    itemInvoiced: {
      name: t('finance.tip.invoiced.name'),
      what: t('finance.tip.col.itemInvoiced.what'),
      calc: t('finance.tip.invoiced.calc'),
    },
    itemInvoice: {
      name: t('finance.tip.col.invoice.name'),
      what: t('finance.tip.col.itemInvoice.what'),
      note: t('finance.tip.col.invoice.note'),
    },
  };
  return map[key] ?? { name: key, what: '' };
}

/** ⚠️ One literal key per level — a computed key is invisible to `i18n:check`. */
function seniorityLabel(t: ReturnType<typeof useT>, level: Seniority | null): string {
  if (level === 'junior') return t('person.seniority.junior');
  if (level === 'medior') return t('person.seniority.medior');
  if (level === 'senior') return t('person.seniority.senior');
  if (level === 'lead') return t('person.seniority.lead');
  if (level === 'principal') return t('person.seniority.principal');
  return t('person.seniority.none');
}

/**
 * Where a level sits on the ladder. ⚠️ **Unclassified people sort LAST**, not first: they are a
 * gap in the data rather than the bottom of the scale.
 */
function ladder(key: string): number {
  const at = SENIORITY_LEVELS.indexOf(key as Seniority);
  return at === -1 ? SENIORITY_LEVELS.length : at;
}
